import { useCallback, useEffect, useState } from "react";
import { Search } from "react-bootstrap-table2-toolkit";
import { makeStyles } from "@material-ui/core/styles";

import {
  Box,
  Button,
  Container,
  Card,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
// @material-ui/icons components
import {
  Create,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from "@material-ui/icons";
// @material-ui/lab components
import { Skeleton } from "@material-ui/lab";
// core components
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import FormProfileDialog from "components/Dialogs/FormProfileDialog";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import ToggleCell from "components/Cells/ToggleCell";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import Api from "lib/api/httpRequestHelper";
import { IsMobile } from "utils/is-mobile";

const getTableColumns = (
  setSelectedProfile,
  toggleFormDialog,
  searching,
  toggleAtivo
) => [
  {
    dataField: "IdPerfil",
    text: "IdPerfil",
    sort: true,
    align: "center",
    formatter: (profile) => (searching ? <Skeleton variant="text" /> : profile),
  },
  {
    dataField: "Nome",
    text: "Nome",
    sort: true,
    formatter: (profile) => (searching ? <Skeleton variant="text" /> : profile),
  },
  {
    dataField: "Menus",
    text: "Menus",
    hidden: true,
  },
  {
    dataField: "Ativo",
    text: "Ativo",
    headerStyle: { width: 90 },
    formatter: (cell, row) => {
      return (
        <ToggleCell
          initialValue={row.Ativo}
          handleChange={async (params) => {
            if (row.NrUsuarios === 0) {
              await toggleAtivo({ ...row, ...params });
            } else {
              setSelectedProfile(row);
              toggleFormDialog("active");
            }
          }}
          initialLoading={searching}
          field="Ativo"
          remote={row.NrUsuarios}
        />
      );
    },
    sort: true,
  },
  {
    dataField: "editar",
    text: "",
    isDummyField: true,
    formatter: (cell, row) => {
      return searching ? null : (
        <Tooltip title="Editar Perfil">
          <IconButton
            aria-label="edit"
            onClick={() => {
              setSelectedProfile(row);
              toggleFormDialog("edit");
            }}
          >
            <Box
              component={Create}
              width="1.25rem!important"
              height="1.25rem!important"
            />
          </IconButton>
        </Tooltip>
      );
    },
    headerStyle: { width: 45 },
  },
];

const loadingItems = [
  { IdPerfil: "0" },
  { IdPerfil: "1" },
  { IdPerfil: "2" },
  { IdPerfil: "3" },
  { IdPerfil: "4" },
  { IdPerfil: "5" },
  { IdPerfil: "6" },
  { IdPerfil: "7" },
  { IdPerfil: "8" },
  { IdPerfil: "9" },
];

const { SearchBar } = Search;

const useStyles = makeStyles(componentStyles);

function Perfis() {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const [profiles, setProfiles] = useState(loadingItems);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [searching, setSearching] = useState(true);
  const isMobile = IsMobile();

  const updateList = useCallback(
    async (openFormDialog, updatedProfile) => {
      if (openFormDialog === "delete") {
        const filteredProfile = profiles.filter(
          (profile) => profile.IdPerfil !== updatedProfile.IdPerfil
        );

        setProfiles(filteredProfile);
      } else {
        const newProfileList = profiles?.map((profile) => {
          if (profile.IdPerfil === updatedProfile.IdPerfil)
            return updatedProfile;
          else return profile;
        });

        setProfiles(newProfileList);
      }
    },
    [profiles]
  );

  const toggleAtivo = useCallback(
    async (updatedProfile) => {
      return await Api.postParams(
        "update-profile",
        "adminportal",
        "?nome=" +
          updatedProfile.Nome +
          "&ativo=" +
          updatedProfile.Ativo.toString() +
          "&idPerfil=" +
          updatedProfile.IdPerfil.toString(),
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(
              `Perfil ${res.Result.IdPerfil} foi ${
                res.Result.Ativo ? "ativado" : "inativado"
              } com sucesso!`,
              {
                severity: "success",
              }
            );
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao atualizar perfil ${updatedProfile.IdPerfil}. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });        });
    },
    [snackbar]
  );

  const toggleFormDialog = useCallback((formType) => {
    if (formType) {
      setOpenFormDialog(formType);
    }
  }, []);

  const fetchList = async () => {
    await Api.get("list-profile", "adminportal", "", true)
      .then(({ Success, Result }) => {
        if (Success) {
          setProfiles(Result);
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });      })
      .finally(() => setSearching(false));
  };

  const handleRefresh = async () => {
    setSearching(true);

    return fetchList();
  };

  useEffect(() => {
    fetchList();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <HeaderClientes section="Perfis" subsection="Administração" />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <ToolkitTable
                keyField="IdPerfil"
                dataTable={searching ? loadingItems : profiles}
                columnModel={getTableColumns(
                  setSelectedProfile,
                  toggleFormDialog,
                  searching,
                  toggleAtivo
                )}
                search
                allowEmpty
                CustomHeader={({ searchProps }) => (
                  <Grid
                    container
                    className="pl-4"
                    style={{ padding: isMobile ? "0 24px 10px" : "0 24px" }}
                  >
                    <Grid item xs={isMobile ? 12 : 4}>
                      <Box
                        component={Typography}
                        variant="h2"
                        marginBottom="0!important"
                      >
                        <Box component="span">Lista de Perfis</Box>
                        <IconButtonLoader
                          size="medium"
                          extraStyle={{
                            width: 24,
                            height: 24,
                            marginLeft: "0.3rem",
                          }}
                          title="Recarregar lista"
                          onClick={handleRefresh}
                          icon={<RefreshIcon color="primary" />}
                          aria-label="refresh"
                          color="primary"
                          disabled={searching}
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={isMobile ? 12 : 8}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: isMobile ? "flex-start" : "flex-end",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <SearchBar
                        className="form-control"
                        placeholder="Pesquisar Perfil"
                        {...searchProps}
                        disabled={searching}
                      />
                      <Box
                        justifyContent="flex-end"
                        display="flex"
                        flexWrap="wrap"
                        style={{ marginLeft: isMobile ? 0 : 15 }}
                      >
                        <Button
                          variant="contained"
                          classes={{
                            root: classes.buttonRootWhite,
                          }}
                          onClick={() => toggleFormDialog("create")}
                          disabled={searching}
                        >
                          <Box
                            component={AddIcon}
                            position="relative"
                            top="-2px"
                          />{" "}
                          Criar
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                pagination={{
                  options: [25, 50, 100],
                  defaultOption: 25,
                }}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
      {Boolean(openFormDialog) && (
        <FormProfileDialog
          open
          formType={openFormDialog}
          onClose={(profile) => {
            setOpenFormDialog(null);
            setSelectedProfile(null);
            if (profile?.IdPerfil > 0) {
              if (openFormDialog === "create") {
                const newProfileList = [...profiles, { ...profile }];

                setProfiles(newProfileList);
              } else {
                updateList(openFormDialog, profile);
              }
            }
          }}
          selectedProfile={selectedProfile}
          toggleAtivo={toggleAtivo}
        />
      )}
    </>
  );
}

Perfis.propTypes = {};

export default Perfis;
